import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import OtherPayablesForm from './OtherPayablesForm';
import OfficePayablesForm from './OfficePayablesForm';
import PopoutWindow from '../common/PopoutWindow';
import ConfirmDialog from '../common/ConfirmDialog';
import Select2Dropdown from '../common/Select2Dropdown';
import { usePermissions } from '../../services/PermissionsContext';
import '../client/ClientPortal.css';
import { TRANSPORTER_INVOICING_CONFIG } from '../../transporterPayableChargesConfig';

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
};

const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('kk_token')}`,
    'Content-Type': 'application/json',
});

const toISODate = (value) => {
    if (!value) return null;
    let s;
    if (value instanceof Date) {
        s = value.toISOString().split('T')[0];
    } else {
        s = value.toString().split('T')[0].replace(/\//g, '-').trim();
    }
    const parts = s.split('-');
    if (parts.length !== 3) return null;
    const [a, b, c] = parts;
    if (a.length === 4) {
        return `${a}-${b.padStart(2, '0')}-${c.padStart(2, '0')}`;
    }
    return `${c}-${b.padStart(2, '0')}-${a.padStart(2, '0')}`;
};

const filterPendingByReleaseDate = (rows) => {
    const from = toISODate(TRANSPORTER_INVOICING_CONFIG.PENDING_RELEASE_DATE_FROM);
    if (!from) return rows;
    return rows.filter(r => r.release_date && toISODate(r.release_date) >= from);
};

const TABS = [
    { key: 'pending', label: 'Pending' },
    { key: 'submitted', label: 'Submitted' },
    { key: 'approved', label: 'Approved' },
    { key: 'rejected', label: 'Rejected' },
    { key: 'client_paid', label: 'Client Paid' },
];

const FILTER_MODES = [
    { id: 'daily', label: 'Daily' },
    { id: 'minus1d', label: '-1D' },
    { id: 'minus1w', label: '-1W' },
    { id: 'minus1m', label: '-1M' },
    { id: 'range', label: 'Date Range' },
    { id: 'search', label: 'Lookup' },
];

const FILTER_SEARCH_OPTIONS = [
    { id: 'idf_no', text: 'IDF #' },
    { id: 'bl', text: 'BL #' },
    { id: 'customer', text: 'Customer' },
    { id: 'invoice_no', text: 'Invoice #' },
    { id: 'reference_no', text: 'REF #' },
];

const TransporterInvoicingAdmin = () => {
    const { hasPermission } = usePermissions();
    const [activeTab, setActiveTab] = useState('pending');
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [expanded, setExpanded] = useState({});
    const [transporterOptions, setTransporterOptions] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedTransporter, setSelectedTransporter] = useState(null);
    const [payableOpen, setPayableOpen] = useState(false);
    const [payableMode, setPayableMode] = useState('add');
    const [selectedJob, setSelectedJob] = useState(null);
    const [payableModule, setPayableModule] = useState('ACCOUNTS');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [tabCounts, setTabCounts] = useState(Object.fromEntries(TABS.map(t => [t.key, 0])));
    const [rejectedFlash, setRejectedFlash] = useState(false);
    const [filterMode, setFilterMode] = useState('daily');
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [filterSearchParam, setFilterSearchParam] = useState('');
    const [filterSearch, setFilterSearch] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, row: null });
    const [clientPaidConfirm, setClientPaidConfirm] = useState({ open: false, row: null });

    const canCreate = hasPermission('TRANSPORTER INVOICING', 'can_create');
    const canEdit = hasPermission('TRANSPORTER INVOICING', 'can_edit');
    const canDelete = hasPermission('TRANSPORTER INVOICING', 'can_delete');

    useEffect(() => {
        if (activeTab === 'approved') {
            setSortConfig({ key: 'approved_at_raw', direction: 'desc' });
        } else if (activeTab === 'submitted' || activeTab === 'rejected' || activeTab === 'client_paid') {
            setSortConfig({ key: 'submitted_at_raw', direction: 'desc' });
        } else {
            setSortConfig({ key: null, direction: 'asc' });
        }
        setFilterMode('daily');
        setFilterSearch('');
        setFilterSearchParam('');
        setFilterStartDate('');
        setFilterEndDate('');
    }, [activeTab]);

    useEffect(() => {
        const fetchTransporters = async () => {
            try {
                const res = await fetch('/api/masters/transporters/', { headers: getAuthHeaders() });
                if (!res.ok) throw new Error('Failed to load transporters.');
                const data = await res.json();
                const options = (data.results || data || []).map(t => ({ id: t.id, text: t.name || t.text }));
                setTransporterOptions(options);
            } catch (error) {
                console.error('Failed to load transporters', error);
            }
        };
        fetchTransporters();
    }, []);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const res = await fetch('/api/admin/users/', { headers: getAuthHeaders() });
                if (!res.ok) throw new Error('Failed to load users.');
                const data = await res.json();
                setUsers(Array.isArray(data) ? data : (data.results || []));
            } catch (error) {
                console.error('Failed to load users', error);
            }
        };
        fetchUsers();
    }, []);

    const fetchTabCounts = async () => {
        if (!selectedTransporter?.id) return;
        const newCounts = {};
        await Promise.all(TABS.map(async (tab) => {
            let url;
            if (tab.key === 'pending') {
                url = `/api/admin/transporter-invoicing/pending/?transporter=${selectedTransporter.id}`;
            } else if (tab.key === 'client_paid') {
                url = `/api/admin/transporter-invoicing/client-paid/?transporter=${selectedTransporter.id}`;
            } else {
                url = `/api/admin/transporter-invoicing/invoices/?status=${tab.key.toUpperCase()}&transporter=${selectedTransporter.id}`;
            }
            try {
                const res = await fetch(url, { headers: getAuthHeaders() });
                if (res.ok) {
                    const data = await res.json();
                    const allResults = data.results || [];
                    newCounts[tab.key] = tab.key === 'pending' ? filterPendingByReleaseDate(allResults).length : allResults.length;
                }
            } catch (error) {
                console.error('Failed to load tab count', tab.key, error);
            }
        }));
        setTabCounts(newCounts);
    };

    const fetchData = async (silent = false) => {
        if (!selectedTransporter?.id) return;
        if (!silent) setLoading(true);
        try {
            let url;
            if (activeTab === 'pending') {
                url = `/api/admin/transporter-invoicing/pending/?transporter=${selectedTransporter.id}`;
            } else if (activeTab === 'client_paid') {
                url = `/api/admin/transporter-invoicing/client-paid/?transporter=${selectedTransporter.id}`;
            } else {
                url = `/api/admin/transporter-invoicing/invoices/?status=${activeTab.toUpperCase()}&transporter=${selectedTransporter.id}`;
            }
            const res = await fetch(url, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error('Failed to load data.');
            const data = await res.json();
            const allResults = data.results || [];
            const filtered = activeTab === 'pending' ? filterPendingByReleaseDate(allResults) : allResults;
            setRows(filtered);
            setTabCounts(prev => ({ ...prev, [activeTab]: filtered.length }));
        } catch (error) {
            toast.error(error.message || 'Failed to load data.');
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, [activeTab, selectedTransporter]);

    useEffect(() => {
        if (tabCounts.rejected > 0) {
            const id = setInterval(() => setRejectedFlash(f => !f), 600);
            return () => clearInterval(id);
        }
        setRejectedFlash(false);
    }, [tabCounts.rejected]);

    useEffect(() => {
        if (selectedTransporter?.id) fetchTabCounts();
    }, [selectedTransporter]);

    const toggleDetails = (id) => {
        setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const openPayableForm = (row, module = 'ACCOUNTS', mode = 'add') => {
        setSelectedJob(row);
        setPayableModule(module);
        setPayableMode(mode);
        setPayableOpen(true);
    };

    const handleDelete = (row) => {
        setDeleteConfirm({ open: true, row });
    };

    const executeDelete = async () => {
        const row = deleteConfirm.row;
        if (!row) return;
        try {
            const res = await fetch(`/api/admin/transporter-invoicing/client-paid/?id=${row.id}`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });
            if (res.ok) {
                toast.success('Client Paid entry removed.');
                fetchData(true);
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.error || 'Failed to remove Client Paid entry.');
            }
        } catch (error) {
            toast.error(error.message || 'Failed to remove Client Paid entry.');
        } finally {
            setDeleteConfirm({ open: false, row: null });
        }
    };

    const handleClientPaid = (row) => {
        setClientPaidConfirm({ open: true, row });
    };

    const executeClientPaid = async () => {
        const row = clientPaidConfirm.row;
        if (!row) return;
        try {
            const res = await fetch(`/api/admin/transporter-invoicing/client-paid/?transporter=${selectedTransporter.id}`, {
                method: 'POST',
                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ job: row.id }),
            });
            if (res.ok) {
                toast.success('Job marked as Client Paid.');
                fetchData(true);
                fetchTabCounts();
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.error || 'Failed to mark Client Paid.');
            }
        } catch (error) {
            toast.error(error.message || 'Failed to mark Client Paid.');
        } finally {
            setClientPaidConfirm({ open: false, row: null });
        }
    };

    const handlePayableClose = () => {
        setPayableOpen(false);
        setSelectedJob(null);
        setPayableModule('ACCOUNTS');
        setPayableMode('add');
    };

    const handlePayableSave = () => {
        toast.success('Payable saved.');
        handlePayableClose();
        fetchData(true);
    };

    const renderFilterBar = () => {
        if (activeTab === 'pending') return null;
        return (
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: 16, flexWrap: 'wrap' }}>
                <div>
                    <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 3 }}>VIEW MODE</label>
                    <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: '1px solid #ced4da' }}>
                        {FILTER_MODES.map(m => (
                            <button
                                key={m.id}
                                onClick={() => setFilterMode(m.id)}
                                style={{
                                    padding: '6px 14px', border: 'none', cursor: 'pointer', fontSize: '0.85rem',
                                    background: filterMode === m.id ? '#1a73e8' : '#fff',
                                    color: filterMode === m.id ? '#fff' : '#333',
                                    fontWeight: filterMode === m.id ? 600 : 400,
                                }}
                            >{m.label}</button>
                        ))}
                    </div>
                </div>
                {filterMode === 'range' && (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <label style={{ marginBottom: 0, fontSize: '0.9em' }}>From:</label>
                            <input type="date" className="form-control" value={filterStartDate} onChange={e => setFilterStartDate(e.target.value)} style={{ width: '120px' }} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <label style={{ marginBottom: 0, fontSize: '0.9em' }}>To:</label>
                            <input type="date" className="form-control" value={filterEndDate} onChange={e => setFilterEndDate(e.target.value)} style={{ width: '120px' }} />
                        </div>
                    </>
                )}
                {filterMode === 'search' && (
                    <>
                        <div style={{ width: '150px' }}>
                            <Select2Dropdown
                                options={FILTER_SEARCH_OPTIONS}
                                value={filterSearchParam}
                                onChange={(val) => setFilterSearchParam(val)}
                                placeholder="Select Field..."
                                valueKey="id"
                                displayKey="text"
                            />
                        </div>
                        <input
                            type="text"
                            className="form-control"
                            value={filterSearch}
                            onChange={e => setFilterSearch(e.target.value)}
                            placeholder={`Search ${FILTER_SEARCH_OPTIONS.find(o => o.id === filterSearchParam)?.text || 'Term'}...`}
                            disabled={!filterSearchParam}
                            style={{ width: '150px' }}
                        />
                    </>
                )}
            </div>
        );
    };

    const renderApprovedSummary = () => {
        if (activeTab !== 'approved') return null;
        const toDateCount = rows.length;
        const toDateTotal = rows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
        const rangeCount = displayRows.length;
        const rangeTotal = displayRows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
        const today = todayISO();
        const toDateStr = new Date(`${today}T00:00:00`).toLocaleDateString('en-GB');
        const rangeStart = filterStartDate ? new Date(`${filterStartDate}T00:00:00`).toLocaleDateString('en-GB') : '—';
        const rangeEnd = filterEndDate ? new Date(`${filterEndDate}T00:00:00`).toLocaleDateString('en-GB') : '—';
        let rangeLabel = null;
        if (filterMode === 'range') {
            rangeLabel = `${rangeStart} → ${rangeEnd}`;
        } else if (filterMode === 'daily') {
            rangeLabel = toDateStr;
        } else if (filterMode === 'minus1d') {
            rangeLabel = `${new Date(`${addDays(today, -1)}T00:00:00`).toLocaleDateString('en-GB')} → ${toDateStr}`;
        } else if (filterMode === 'minus1w') {
            rangeLabel = `${new Date(`${addDays(today, -7)}T00:00:00`).toLocaleDateString('en-GB')} → ${toDateStr}`;
        } else if (filterMode === 'minus1m') {
            rangeLabel = `${new Date(`${addDays(today, -30)}T00:00:00`).toLocaleDateString('en-GB')} → ${toDateStr}`;
        }
        return (
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ background: '#fff3e0', border: '1px solid #ffcc80', borderRadius: 8, padding: '10px 16px', minWidth: 180 }}>
                    <div style={{ fontSize: 11, color: '#e65100', fontWeight: 700, marginBottom: 4 }}>TO DATE</div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#bf360c', marginBottom: 6 }}>{toDateStr}</div>
                    <div style={{ fontSize: '0.85rem', color: '#333' }}>INVOICES - {toDateCount}</div>
                    <div style={{ fontSize: '0.85rem', color: '#333', fontWeight: 700 }}>TZS - {formatCurrency(toDateTotal)}</div>
                </div>
                {rangeLabel && (
                    <div style={{ background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 8, padding: '10px 16px', minWidth: 200 }}>
                        <div style={{ fontSize: 11, color: '#1565c0', fontWeight: 700, marginBottom: 4 }}>DATE RANGE</div>
                        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0d47a1', marginBottom: 6 }}>{rangeLabel}</div>
                        <div style={{ fontSize: '0.85rem', color: '#333' }}>INVOICES - {rangeCount}</div>
                        <div style={{ fontSize: '0.85rem', color: '#333', fontWeight: 700 }}>TZS - {formatCurrency(rangeTotal)}</div>
                    </div>
                )}
            </div>
        );
    };

    const renderTabs = () => {
        const getTabStyle = (tab) => {
            const isActive = activeTab === tab.key;
            const isRejected = tab.key === 'rejected';
            const isClientPaid = tab.key === 'client_paid';
            const count = tabCounts[tab.key] || 0;

            const baseStyle = {
                padding: '10px 18px',
                border: '1px solid #e2e8f0',
                borderBottom: 'none',
                borderTopLeftRadius: 6,
                borderTopRightRadius: 6,
                fontWeight: 600,
                fontSize: '0.9rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                transition: 'background 0.15s, color 0.15s, border-color 0.15s',
            };

            if (isRejected || isClientPaid) {
                if (isActive) {
                    return {
                        ...baseStyle,
                        background: isClientPaid ? '#15803d' : (rejectedFlash ? '#b91c1c' : '#dc2626'),
                        color: '#fff',
                        borderColor: isClientPaid ? '#15803d' : '#dc2626',
                    };
                }
                return {
                    ...baseStyle,
                    background: isClientPaid ? '#dcfce7' : (rejectedFlash ? '#fee2e2' : '#fef2f2'),
                    color: isClientPaid ? '#15803d' : '#dc2626',
                    borderColor: isClientPaid ? '#86efac' : '#fecaca',
                };
            }

            if (isActive) {
                return {
                    ...baseStyle,
                    background: '#fff',
                    color: tab.key === 'approved' ? '#16a34a' : tab.key === 'submitted' ? '#7c3aed' : '#2563eb',
                    borderTop: `2px solid ${tab.key === 'approved' ? '#16a34a' : tab.key === 'submitted' ? '#7c3aed' : '#2563eb'}`,
                };
            }

            return {
                ...baseStyle,
                background: 'transparent',
                color: '#64748b',
            };
        };

        return (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 16, borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', gap: 8 }}>
                    {TABS.map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            style={getTabStyle(tab)}
                        >
                            <span>{tab.label}</span>
                            {typeof tabCounts[tab.key] === 'number' && (
                                <span style={{
                                    background: activeTab === tab.key ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.08)',
                                    color: activeTab === tab.key ? 'inherit' : '#334155',
                                    borderRadius: 999,
                                    padding: '2px 8px',
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    minWidth: 22,
                                    textAlign: 'center',
                                }}>
                                    {tabCounts[tab.key]}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
                {renderApprovedSummary()}
            </div>
        );
    };

    const renderContainerTable = (containers) => {
        return (
            <div style={{ flex: '0 0 auto', overflowX: 'auto' }}>
                <table className="cp-table" style={{ width: 'auto', tableLayout: 'auto' }}>
                    <thead>
                        <tr>
                            <th style={{ ...cellBorder, textAlign: 'center', whiteSpace: 'nowrap' }}>SN</th>
                            <th style={{ ...cellBorder, textAlign: 'center', whiteSpace: 'nowrap' }}>CONTAINER NUMBER</th>
                        </tr>
                    </thead>
                    <tbody>
                        {containers.map((container, idx) => {
                            const sizeText = [container.size, container.container_type].filter(Boolean).join(' ').trim();
                            return (
                                <tr key={container.id || idx}>
                                    <td style={{ ...cellBorder, textAlign: 'center', whiteSpace: 'nowrap' }}>{idx + 1}</td>
                                    <td style={{ ...cellBorder, textAlign: 'center', fontWeight: 'bold', color: 'red', whiteSpace: 'nowrap' }}>
                                        {(container.container_no || '—').toString().toUpperCase()}
                                        {sizeText ? ' | ' + sizeText.toUpperCase() : ''}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderPendingRow = (row, idx) => {
        const isOpen = !!expanded[row.id];
        const rowBg = idx % 2 === 0 ? '#fff' : '#f8fafc';
        const disabled = !selectedTransporter?.id || !canCreate;
        return (
            <React.Fragment key={row.id}>
                <tr style={{ backgroundColor: rowBg }}>
                    <td style={{ ...cellBorder, whiteSpace: 'nowrap', minWidth: 100 }}>{(row.idf_no || '—').toString().toUpperCase()}</td>
                    <td style={cellBorder}>{(row.bl || '—').toString().toUpperCase()}</td>
                    <td style={{ ...cellBorder, fontWeight: 'bold' }}>{(row.customer || '—').toString().toUpperCase()}</td>
                    <td style={{ ...cellBorder, whiteSpace: 'nowrap', minWidth: 80 }}>{(row.container_size || '—').toString().toUpperCase()}</td>
                    <td style={{ ...cellBorder, whiteSpace: 'nowrap', minWidth: 160 }}>{(row.icd_short_name || row.icd || '—').toString().toUpperCase()}</td>
                    <td style={cellBorder}>{(row.release_date || '—').toString().toUpperCase()}</td>
                    <td style={cellBorder}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                            {canCreate && (
                                <button
                                    onClick={() => openPayableForm(row)}
                                    disabled={disabled}
                                    style={{ background: '#2563eb', color: '#fff', padding: '4px 8px', fontSize: '0.75rem', border: 'none', borderRadius: 4, whiteSpace: 'nowrap' }}
                                >
                                    Create Invoice
                                </button>
                            )}
                            {canCreate && (
                                <button
                                    onClick={() => handleClientPaid(row)}
                                    disabled={disabled}
                                    style={{ background: '#f97316', color: '#fff', padding: '4px 8px', fontSize: '0.75rem', border: 'none', borderRadius: 4, whiteSpace: 'nowrap' }}
                                >
                                    Client Paid
                                </button>
                            )}
                        </div>
                    </td>
                    <td style={cellBorder}>
                        <button
                            onClick={() => toggleDetails(row.id)}
                            className="cp-btn cp-btn-secondary"
                            style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                        >
                            {isOpen ? 'Hide' : 'Details'}
                        </button>
                    </td>
                </tr>
                {isOpen && (
                    <tr>
                        <td colSpan={8} style={{ ...cellBorder, background: '#fff0f0' }}>
                            <div style={{ padding: 16, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                                {renderContainerTable(row.containers)}
                                {row.customer_ref && (
                                    <div style={{ minWidth: 260, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <span style={{ fontWeight: 'bold' }}>Client Reference No.: {row.customer_ref.toString().toUpperCase()}</span>
                                    </div>
                                )}
                            </div>
                        </td>
                    </tr>
                )}
            </React.Fragment>
        );
    };

    const renderPayableRow = (row, idx) => {
        const rowBg = idx % 2 === 0 ? '#fff' : '#f8fafc';
        return (
            <tr key={row.id} style={{ backgroundColor: rowBg }}>
                <td style={{ ...cellBorder, whiteSpace: 'nowrap', minWidth: 160 }}>{(row.idf_no || '—').toString().toUpperCase()}</td>
                <td style={cellBorder}>{(row.bl || '—').toString().toUpperCase()}</td>
                <td style={{ ...cellBorder, fontWeight: 'bold' }}>{(row.customer || '—').toString().toUpperCase()}</td>
                <td style={{ ...cellBorder, whiteSpace: 'nowrap', minWidth: 80 }}>{(row.container_size || '—').toString().toUpperCase()}</td>
                <td style={{ ...cellBorder, textAlign: 'right' }}>{(formatCurrency(row.amount) || '').toString().toUpperCase()}</td>
                {activeTab === 'approved' ? (
                    <>
                        <td style={cellBorder}>{(row.cheque_no || '—').toString().toUpperCase()}</td>
                        <td style={cellBorder}>{(row.collected_by || '—').toString().toUpperCase()}</td>
                        <td style={cellBorder}>{(row.approved_at || '—').toString().toUpperCase()}</td>
                    </>
                ) : (
                    <>
                        <td style={cellBorder}>{(row.submitted_at || '—').toString().toUpperCase()}</td>
                        {activeTab === 'rejected' && (
                            <td style={{ ...cellBorder, color: '#dc2626', fontWeight: 'bold' }}>{(row.rejected_reason || '—').toString().toUpperCase()}</td>
                        )}
                    </>
                )}
                <td style={cellBorder}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                        {activeTab === 'client_paid' ? (
                            canDelete && (
                                <button
                                    className="icon-btn"
                                    style={{ color: '#dc2626', padding: '4px' }}
                                    title="Remove Client Paid"
                                    onClick={() => handleDelete(row)}
                                >
                                    <i className="fa fa-trash" />
                                </button>
                            )
                        ) : activeTab === 'rejected' ? (
                            canEdit && (
                                <button
                                    className="icon-btn"
                                    style={{ color: '#facc15', padding: '4px' }}
                                    title="Edit payable"
                                    onClick={() => openPayableForm(row, row.module || 'ACCOUNTS', 'edit')}
                                >
                                    <i className="fa fa-pencil" />
                                </button>
                            )
                        ) : (
                            <button
                                className="icon-btn"
                                style={{ color: '#2563eb', padding: '4px' }}
                                title="View payable"
                                onClick={() => openPayableForm(row, row.module || 'ACCOUNTS', 'view')}
                            >
                                <i className="fa fa-eye" />
                            </button>
                        )}
                    </div>
                </td>
                <td style={cellBorder}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                        {activeTab === 'client_paid' ? null : (
                            canCreate && (
                                <>
                                    <button
                                        onClick={() => openPayableForm(row, 'ACCOUNTS', 'add')}
                                        style={{ background: '#2563eb', color: '#fff', padding: '4px 8px', fontSize: '0.75rem', border: 'none', borderRadius: 4, whiteSpace: 'nowrap' }}
                                    >Add Invoice</button>
                                    <button
                                        onClick={() => openPayableForm(row, 'OFFICE', 'add')}
                                        style={{ background: '#16a34a', color: '#fff', padding: '4px 8px', fontSize: '0.75rem', border: 'none', borderRadius: 4, whiteSpace: 'nowrap' }}
                                    >Add Cash</button>
                                </>
                            )
                        )}
                    </div>
                </td>
            </tr>
        );
    };

    const handleSort = (key) => {
        if (!key) return;
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const sortIcon = (key) => {
        if (sortConfig.key !== key) return ' ⇅';
        return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    };

    const cellBorder = { border: '1px solid #cbd5e1' };

    const columns = useMemo(() => {
        if (activeTab === 'pending') {
            return [
                { key: 'idf_no', label: 'IDF' },
                { key: 'bl', label: 'BL' },
                { key: 'customer', label: 'CUSTOMER' },
                { key: 'container_size', label: 'CONTAINER SIZE' },
                { key: 'icd', label: 'ICD' },
                { key: 'release_date', label: 'RELEASE DATE' },
                { key: null, label: 'ACTION', sortable: false },
                { key: null, label: 'DETAILS', sortable: false },
            ];
        }
        if (activeTab === 'approved') {
            return [
                { key: 'idf_no', label: 'IDF' },
                { key: 'bl', label: 'BL' },
                { key: 'customer', label: 'CUSTOMER' },
                { key: 'container_size', label: 'CONTAINER SIZE' },
                { key: 'amount', label: 'AMOUNT' },
                { key: 'cheque_no', label: 'CHEQUE REF NO' },
                { key: 'collected_by', label: 'COLLECTED BY' },
                { key: 'approved_at_raw', label: 'APPROVED DATE' },
                { key: null, label: 'VIEW', sortable: false },
                { key: null, label: 'ADDITIONAL', sortable: false },
            ];
        }
        if (activeTab === 'rejected') {
            return [
                { key: 'idf_no', label: 'IDF' },
                { key: 'bl', label: 'BL' },
                { key: 'customer', label: 'CUSTOMER' },
                { key: 'container_size', label: 'CONTAINER SIZE' },
                { key: 'amount', label: 'AMOUNT' },
                { key: 'submitted_at_raw', label: 'SUBMITTED DATE' },
                { key: 'rejected_reason', label: 'REJECTED REASON' },
                { key: null, label: 'VIEW / EDIT', sortable: false },
                { key: null, label: 'ADDITIONAL', sortable: false },
            ];
        }
        if (activeTab === 'client_paid') {
            return [
                { key: 'idf_no', label: 'IDF' },
                { key: 'bl', label: 'BL' },
                { key: 'customer', label: 'CUSTOMER' },
                { key: 'container_size', label: 'CONTAINER SIZE' },
                { key: 'amount', label: 'AMOUNT' },
                { key: 'submitted_at_raw', label: 'PAID AT' },
                { key: null, label: 'DELETE', sortable: false },
                { key: null, label: 'ADDITIONAL', sortable: false },
            ];
        }
        return [
            { key: 'idf_no', label: 'IDF' },
            { key: 'bl', label: 'BL' },
            { key: 'customer', label: 'CUSTOMER' },
            { key: 'container_size', label: 'CONTAINER SIZE' },
            { key: 'amount', label: 'AMOUNT' },
            { key: 'submitted_at_raw', label: 'SUBMITTED DATE' },
            { key: null, label: 'VIEW', sortable: false },
            { key: null, label: 'ADDITIONAL', sortable: false },
        ];
    }, [activeTab]);

    const sortedRows = useMemo(() => {
        if (!sortConfig.key) return rows;
        return [...rows].sort((a, b) => {
            if (sortConfig.key === 'amount') {
                const aVal = parseFloat(a.amount) || 0;
                const bVal = parseFloat(b.amount) || 0;
                return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
            }
            const aVal = (a[sortConfig.key] ?? '').toString();
            const bVal = (b[sortConfig.key] ?? '').toString();
            const cmp = aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
            return sortConfig.direction === 'asc' ? cmp : -cmp;
        });
    }, [rows, sortConfig]);

    const todayISO = () => new Date().toISOString().split('T')[0];
    const addDays = (dateStr, days) => {
        if (!dateStr) return '';
        const [y, m, d] = dateStr.split('-').map(Number);
        const date = new Date(y, m - 1, d);
        date.setDate(date.getDate() + days);
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const displayRows = useMemo(() => {
        if (activeTab === 'pending') return sortedRows;
        return sortedRows.filter(row => {
            if (filterMode === 'search') {
                if (!filterSearchParam || !filterSearch) return true;
                const val = (row[filterSearchParam] || '').toString().toLowerCase();
                return val.includes(filterSearch.toLowerCase());
            }
            const rawDate = activeTab === 'approved' ? row.approved_at_raw : row.submitted_at_raw;
            const date = rawDate ? rawDate.slice(0, 10) : '';
            if (filterMode === 'daily') {
                return date === todayISO();
            }
            if (filterMode === 'minus1d') {
                return date >= addDays(todayISO(), -1) && date <= todayISO();
            }
            if (filterMode === 'minus1w') {
                return date >= addDays(todayISO(), -7) && date <= todayISO();
            }
            if (filterMode === 'minus1m') {
                return date >= addDays(todayISO(), -30) && date <= todayISO();
            }
            if (filterMode === 'range') {
                const after = !filterStartDate || date >= filterStartDate;
                const before = !filterEndDate || date <= filterEndDate;
                return after && before;
            }
            return true;
        });
    }, [sortedRows, activeTab, filterMode, filterSearchParam, filterSearch, filterStartDate, filterEndDate]);

    const payableInitialData = useMemo(() => {
        if (!selectedJob) return null;
        const transporterId = selectedTransporter?.id;
        const transporterName = selectedTransporter?.text;
        if (payableMode === 'edit' || payableMode === 'view') {
            return {
                ...selectedJob,
                job: selectedJob.job_id,
                job_number: selectedJob.job_id,
                idf_number: selectedJob.idf_no,
                bl_number: selectedJob.bl,
                vendor_staff_type: 'VENDOR',
                vendor: selectedJob.transporter || transporterId,
                transporter: transporterId,
                transporter_name: transporterName,
                vendor_name: transporterName,
                container_summary: selectedJob.container_summary,
                containers: selectedJob.containers,
            };
        }
        return {
            job: selectedJob.job_id,
            job_number: selectedJob.job_id,
            idf_number: selectedJob.idf_no,
            bl_number: selectedJob.bl,
            customer_name: selectedJob.customer,
            customer_tin: selectedJob.customer_tin,
            file_no: selectedJob.file_no,
            vendor_staff_type: 'VENDOR',
            vendor: transporterId,
            transporter: transporterId,
            transporter_name: transporterName,
            vendor_name: transporterName,
            invoice_type: 'TAX',
            module: payableModule,
            container_summary: selectedJob.container_summary,
            containers: selectedJob.containers,
        };
    }, [selectedJob, payableMode, selectedTransporter, payableModule]);

    const filteredTransporterOptions = useMemo(() => {
        const linkedIds = new Set(
            users
                .filter(u => u.profile?.linked_transporter)
                .map(u => u.profile.linked_transporter)
        );
        return transporterOptions.filter(t => linkedIds.has(t.id));
    }, [transporterOptions, users]);

    const onSelectTransporter = (val) => {
        const opt = filteredTransporterOptions.find(t => String(t.id) === String(val));
        setSelectedTransporter(opt || null);
        setActiveTab('pending');
        setExpanded({});
    };

    return (
        <div className="cp-card" style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ margin: 0 }}>Transporter Invoicing</h2>
                <button
                    type="button"
                    className="icon-btn"
                    onClick={() => { if (selectedTransporter?.id) { fetchData(true); fetchTabCounts(); } }}
                    title="Refresh"
                    style={{ fontSize: '1.1rem' }}
                >
                    <i className="fa fa-refresh" />
                </button>
            </div>
            <div style={{ marginBottom: 16, maxWidth: 420 }}>
                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 6 }}>TRANSPORTER</label>
                <Select2Dropdown
                    options={filteredTransporterOptions}
                    value={selectedTransporter?.id || ''}
                    onChange={onSelectTransporter}
                    valueKey="id"
                    displayKey="text"
                    placeholder="SELECT TRANSPORTER"
                />
            </div>
            {renderTabs()}
            {renderFilterBar()}
            {loading ? (
                <div className="cp-loader">Loading…</div>
            ) : (
                <div className="cp-table-wrap">
                    {!selectedTransporter ? (
                        <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8' }}>Please select a transporter to view records.</div>
                    ) : (
                        <table className="cp-table" style={{ borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    {columns.map(col => (
                                        <th
                                            key={col.label}
                                            onClick={col.sortable !== false ? () => handleSort(col.key) : undefined}
                                            style={{ ...cellBorder, cursor: col.sortable !== false ? 'pointer' : 'default', whiteSpace: col.key === 'container_size' ? 'normal' : 'nowrap', minWidth: col.key === 'idf_no' || col.key === 'icd' ? 160 : col.key === 'container_size' ? 80 : undefined }}
                                            title={col.sortable !== false ? `Sort by ${col.label}` : undefined}
                                        >
                                            {col.label}{col.sortable !== false && sortIcon(col.key)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {displayRows.length === 0 ? (
                                    <tr>
                                        <td colSpan={columns.length} className="cp-empty" style={{ ...cellBorder, textAlign: 'center', color: '#94a3b8' }}>
                                            No records found.
                                        </td>
                                    </tr>
                                ) : (
                                    activeTab === 'pending'
                                        ? displayRows.map((row, idx) => renderPendingRow(row, idx))
                                        : displayRows.map((row, idx) => renderPayableRow(row, idx))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {payableOpen && selectedJob && (
                <PopoutWindow
                    title={`Payable - ${selectedJob?.idf_no || selectedJob?.id || ''}`}
                    name={`payable_window_${selectedJob?.id || Date.now()}`}
                    onClose={handlePayableClose}
                    toastContainerId="payable-popout-toasts"
                >
                    {payableModule === 'OFFICE' ? (
                        <OfficePayablesForm
                            mode={payableMode}
                            module={payableModule}
                            isTransporter
                            isTransporterPortal
                            adminForTransporter
                            initialData={payableInitialData}
                            onClose={handlePayableClose}
                            onSave={handlePayableSave}
                            popoutMode
                            toastContainerId="payable-popout-toasts"
                        />
                    ) : (
                        <OtherPayablesForm
                            mode={payableMode}
                            module={payableModule}
                            isTransporterPortal
                            initialData={payableInitialData}
                            onClose={handlePayableClose}
                            onSave={handlePayableSave}
                            popoutMode
                            toastContainerId="payable-popout-toasts"
                        />
                    )}
                </PopoutWindow>
            )}

            <ConfirmDialog
                open={deleteConfirm.open}
                title="Confirm Removal"
                message={
                    <span>
                        Are you sure you want to remove the Client Paid entry for IDF # <strong>{(deleteConfirm.row?.idf_no || '').toUpperCase()}</strong>?
                    </span>
                }
                onConfirm={executeDelete}
                onCancel={() => setDeleteConfirm({ open: false, row: null })}
            />

            <ConfirmDialog
                open={clientPaidConfirm.open}
                title="Confirm Client Paid"
                message={
                    <span>
                        Are you sure the Invoice for IDF # <strong>{(clientPaidConfirm.row?.idf_no || '').toUpperCase()}</strong> is paid by Client?
                    </span>
                }
                onConfirm={executeClientPaid}
                onCancel={() => setClientPaidConfirm({ open: false, row: null })}
            />
        </div>
    );
};

export default TransporterInvoicingAdmin;
